import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const chipVariants = cva(
  "inline-flex items-center gap-1 rounded-md border text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "border-transparent bg-chip text-chip-foreground",
        primary: "border-transparent bg-chip-primary text-chip-primary-foreground",
        muted: "border-transparent bg-chip-muted text-chip-muted-foreground",
        outline: "border-chip-outline bg-transparent text-chip-outline-foreground"
      },
      size: {
        sm: "px-1.5 py-0 text-xs [&_svg]:size-3",
        md: "px-2 py-0.5 text-xs [&_svg]:size-3.5"
      }
    },
    defaultVariants: { variant: "default", size: "md" }
  }
);

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof chipVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
}

const RemoveButton = ({ onRemove, label }: { onRemove: () => void; label?: string }) => (
  <button
    type="button"
    aria-label={label ?? "Remove"}
    onClick={(e) => {
      e.stopPropagation();
      onRemove();
    }}
    className="vadium-chip-remove-hit ml-0.5 shrink-0 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring"
  >
    <X className="size-3" />
  </button>
);

const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, variant, size, asChild = false, icon, onRemove, removeLabel, children, ...props }, ref) => {
    if (asChild) {
      return (
        <span ref={ref} className={cn(chipVariants({ variant, size }), "contents", className)}>
          <Slot className={cn(chipVariants({ variant, size }), className)} {...props}>
            {children}
          </Slot>
          {onRemove ? <RemoveButton onRemove={onRemove} label={removeLabel} /> : null}
        </span>
      );
    }
    return (
      <span ref={ref} className={cn(chipVariants({ variant, size }), className)} {...props}>
        {icon ? <span className="shrink-0" aria-hidden="true">{icon}</span> : null}
        {children}
        {onRemove ? <RemoveButton onRemove={onRemove} label={removeLabel} /> : null}
      </span>
    );
  }
);
Chip.displayName = "Chip";

export { Chip, chipVariants };
